import React, { useState } from 'react';
import { methodSteps, recommendations, services, siteImages, testimonials } from '../config/siteConfig';
import { API_BASE_URL, publicApiHeaders } from '../config/apiConfig';

function Home() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: publicApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(formData)
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'No se pudo enviar el mensaje. Inténtalo nuevamente.');
      }

      setFormData({ nombre: '', email: '', mensaje: '' });
      setSubmitStatus({
        type: 'success',
        message: 'Mensaje recibido. Gracias por contactarme; te responderé a la brevedad.'
      });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.message || 'No se pudo enviar el mensaje. Inténtalo nuevamente.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <main className="one-page editorial-site">
      <section id="inicio" className="editorial-hero one-page-section">
        <div className="editorial-hero-copy">
          <p className="eyebrow">Babyplanner en Uruguay</p>
          <h1>Soy Verónica y te ayudo a planificar la llegada de tu bebé con calma, criterio y mucho amor.</h1>
          <p>
            Diseñamos una hoja de ruta para que atravieses el embarazo, la lactancia y el postparto con menos sobreinformación y más seguridad.
          </p>
          <div className="hero-actions">
            <a href="#servicios" className="button button-primary">Quiero saber más</a>
            <a href="#metodo" className="button button-secondary">Ver mi método</a>
          </div>
        </div>

        <figure className="editorial-hero-media">
          <img src={siteImages.hero.src} alt={siteImages.hero.alt} />
        </figure>
      </section>

      <section className="lead-feature one-page-section">
        <div>
          <p className="eyebrow">Querida mamá</p>
          <h2>Di adiós a las noches buscando respuestas sueltas. Este proceso puede sentirse más simple.</h2>
        </div>
        <p>
          Te acompaño a ordenar lo práctico y también lo emocional: qué comprar, cómo preparar la casa, cómo pensar la lactancia, qué rutinas imaginar y qué decisiones merecen hacerse a tu manera.
        </p>
      </section>

      <section id="sobre-mi" className="story-section one-page-section">
        <figure className="story-image tall">
          <img src={siteImages.about.src} alt={siteImages.about.alt} />
        </figure>

        <article className="story-copy">
          <p className="eyebrow">Hola</p>
          <h2>Soy Verónica Echavarría, Babyplanner, odontóloga y asesora en lactancia.</h2>
          <p>
            Acompaño a familias que quieren vivir la llegada de su bebé con organización, contención y decisiones más conscientes. Mi trabajo une planificación, salud, lactancia y una mirada muy humana de esta etapa.
          </p>
          <p>
            No se trata de tener todo perfecto. Se trata de construir un plan posible para tu casa, tu cuerpo, tus tiempos y tu forma de maternar.
          </p>
          <a href="#contacto" className="text-link">Agenda una conversación</a>
        </article>
      </section>

      <section id="metodo" className="method-section one-page-section">
        <div className="method-intro">
          <p className="eyebrow">Mi método</p>
          <h2>Planifica, aprende, siente y organiza siendo tú.</h2>
          <p>
            La personalización es el centro del acompañamiento. Seleccionamos las áreas que importan para tu familia y convertimos cada duda en un siguiente paso claro.
          </p>
        </div>

        <div className="method-grid">
          {methodSteps.map((step, index) => (
            <article className="method-card" key={step.title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h3>{step.title}</h3>
              <p>{step.copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="servicios" className="services-showcase one-page-section">
        <header className="section-header centered-heading">
          <p className="eyebrow">Servicios</p>
          <h2>Elige el acompañamiento que responda a lo que necesitas hoy.</h2>
        </header>

        <div className="service-list">
          {services.map((service, index) => (
            <article className="service-row" key={service.title}>
              <div className="service-number">{String(index + 1).padStart(2, '0')}</div>
              <div>
                <p className="service-subtitle">{service.subtitle}</p>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </div>
              <ul>
                {service.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="full-bleed-cta one-page-section">
        <img src={siteImages.method.src} alt={siteImages.method.alt} />
        <div>
          <p className="eyebrow">¿Comenzamos?</p>
          <h2>Hacerte la vida más fácil en esta aventura es el punto de partida.</h2>
          <a href="#contacto" className="button button-primary">Estoy lista</a>
        </div>
      </section>

      <section id="recomendaciones" className="page-shell one-page-section">
        <div className="narrow-container">
          <header className="section-header">
            <p className="eyebrow">Recursos</p>
            <h2>Recomendaciones para decidir con más calma.</h2>
            <p className="section-lede">
              Productos, tiendas y profesionales que pueden ayudarte a preparar esta etapa con criterio y confianza.
            </p>
          </header>

          <div className="recommendation-stack">
            {recommendations.map((section) => (
              <article className="recommendation-card" key={section.title}>
                <h3>{section.title}</h3>
                <p>{section.copy}</p>
                <ul className="feature-list">
                  {section.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                {section.href && (
                  <a className="text-link" href={section.href} target="_blank" rel="noreferrer">
                    {section.cta}
                  </a>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="testimonios" className="testimonial-band one-page-section">
        <div className="container">
          <header className="section-header centered-heading">
            <p className="eyebrow">Lo que dicen de mí</p>
            <h2>Historias de mamás que se sintieron más acompañadas.</h2>
          </header>

          <div className="testimonials-grid">
            {testimonials.map((testimonial) => (
              <article className="modern-card" key={testimonial.name}>
                <div className="quote-mark">“</div>
                <p>{testimonial.quote}</p>
                <h4>{testimonial.name}</h4>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="contacto" className="contact-split one-page-section">
        <figure>
          <img src={siteImages.contact.src} alt={siteImages.contact.alt} />
        </figure>
        <div className="contact-card">
          <p className="eyebrow">Hablemos</p>
          <h2>¿Dudas? Cuéntame qué necesitas preparar.</h2>
          <p className="section-lede">
            Si quieres saber cómo puedo acompañarte, escríbeme y vemos juntas el mejor punto de partida.
          </p>

          <form onSubmit={handleSubmit} className="form-grid">
            <div className="form-field">
              <label htmlFor="nombre">Nombre</label>
              <input id="nombre" type="text" name="nombre" value={formData.nombre} onChange={handleChange} required />
            </div>

            <div className="form-field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" name="email" value={formData.email} onChange={handleChange} required />
            </div>

            <div className="form-field">
              <label htmlFor="mensaje">Mensaje</label>
              <textarea id="mensaje" name="mensaje" value={formData.mensaje} onChange={handleChange} required />
            </div>

            <button type="submit" className="button button-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Enviando…' : 'Enviar mensaje'}
            </button>

            {submitStatus.message && (
              <p className={`form-status ${submitStatus.type}`} role="status" aria-live="polite">
                {submitStatus.message}
              </p>
            )}
          </form>
        </div>
      </section>
    </main>
  );
}

export default Home;
